/**
 * Every deployment is in Kenya, so a number typed without a country code is a Kenyan one: people write
 * 0712 345 678 or 712345678, never +254. Stored and sent in E.164 (+254 then 7 or 1 and eight digits).
 */
export const KENYA_CALLING_CODE = '+254';

const KENYAN_MOBILE = /^\+254[71]\d{8}$/;

/** Spaces, dashes, dots and brackets are how people group a number, not part of it. */
const GROUPING = /[\s\-().]/g;

function withCallingCode(digits: string): string {
  if (digits.startsWith('+')) return digits;
  if (digits.startsWith('254') && digits.length === 12) return `+${digits}`;
  if (digits.startsWith('0')) return `${KENYA_CALLING_CODE}${digits.slice(1)}`;
  return `${KENYA_CALLING_CODE}${digits}`;
}

/** A Kenyan mobile number however it was typed (0712 345 678, 712345678, 254712345678, +254712345678) in E.164, or null when it is not one. */
export function toKenyanMobile(input: string): string | null {
  const e164 = withCallingCode(input.replace(GROUPING, ''));
  return KENYAN_MOBILE.test(e164) ? e164 : null;
}

/**
 * A contact's phone, which may be a landline or a foreign number: a Kenyan mobile is stored in E.164 so the same
 * number is always written the same way, anything else as it was typed.
 */
export function toContactPhone(input: string): string {
  return toKenyanMobile(input) ?? input.trim();
}

/** The number without +254, for an input that shows the code beside the field. Anything else is returned as it is. */
export function withoutKenyaCallingCode(value: string): string {
  return value.startsWith(KENYA_CALLING_CODE) ? value.slice(KENYA_CALLING_CODE.length) : value;
}

/**
 * The stored form of a search for part of a number typed the local way ("0712 34" → "+25471234"), so it finds
 * numbers kept in E.164. Undefined when the term is not the start of a local number.
 */
export function kenyanMobileSearchTerm(term: string): string | undefined {
  const digits = term.replace(GROUPING, '');
  return /^0[17]\d*$/.test(digits) ? `${KENYA_CALLING_CODE}${digits.slice(1)}` : undefined;
}
